import type { DirectusSchema } from '@/types/directus-types'
import { authentication, createDirectus, rest } from '@directus/sdk'

// default retry settings
export const DIRECTUS_DEFAULT_RETRY_COUNT = 3
export const DIRECTUS_DEFAULT_RETRY_DELAY = 500

// get base path
const elementData = document?.querySelector('powertrip-optimiser')

/** Base URL for the directus services and storage. */
const directusBaseURL =
  elementData?.attributes?.['backend' as any]?.nodeValue ?? 'https://backend.powertrip.earth'

/** Directus asset storage path extension. */
export const directusAssetsRoute = directusBaseURL + '/assets/'

/**
 * Accepted strings for directus collection names.
 *
 * NOTE: there should also be a file handling all interactions with each collection
 * e.g. `asset-bookings-calls.ts` for `"Asset_Bookings"`.
 */
export type CollectionNames =
  | 'Asset_Bookings'
  | 'Car_Record'
  | 'Driver'
  | 'EV_Model_Assets'
  | 'ManagedContent'
  | 'NetworkBranding'
  | 'Optimiser_Settings'
  | 'Favourite_Locations'
  | 'SavedRoutePlans'
  | 'Car_Record_files'
  | 'sales_leads'

export type SingletonNames = 'Company' | 'ScoringConfig'

class LocalStorage {
  get() {
    const storedData = localStorage.getItem('directus-data')
    if (storedData && typeof storedData === 'string') {
      try {
        return JSON.parse(storedData)
      } catch (error) {
        if (import.meta.env.DEV) console.error(error)
        return {}
      }
    }
    return {}
  }
  set(data) {
    localStorage.setItem('directus-data', JSON.stringify(data))
  }
  clear() {
    localStorage.removeItem('directus-data')
  }
}

export const storage = new LocalStorage()
storage.clear() // clear previous sessions data

/** Local directus client. */
const directus = createDirectus<DirectusSchema>(directusBaseURL)
  .with(authentication('json', { storage }))
  .with(
    rest({
      onRequest: (options) => ({
        ...options,
        headers: {
          ...options.headers,
          'cache-control': 'no-store',
        },
      }),
    }),
  )

export default directus
